import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router, UrlCreationOptions } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { Enums } from '../../enums/enums';
import { MediaTypes } from '../../enums/standalone/media-types.enum';
import { LocalStorageHelper } from '../../helpers/local-storage.helper';
import { ModelPwaLaunchTarget } from '../../models/pwa/pwa-launch-target.model';
import { NavigationRoutes } from '../../routes/app.routes.definition';
import { AppMonitoring } from '../monitoring/monitor.app';

/**
 * Remembers the campaign an installed app should open on launch.
 *
 * The PWA manifest has a single static `start_url` (`/`), so an installed app cold-starts on the
 * landing page rather than the campaign it was installed from. On iOS that is handled at install
 * time by `AppInstallTarget`, which makes Safari bookmark the campaign URL itself. This service is
 * the safety net for everywhere else — Android and desktop installs, and any iOS version where the
 * install-time mechanism does not hold. There, storage *is* shared with the browser, so the campaign
 * the user was last looking at can simply be replayed.
 *
 * Every storage access is guarded: `pwaLaunchGuard` runs on the `''` route, so an unhandled throw in
 * here would be a blank screen rather than a degraded feature.
 */
@Injectable({
  providedIn: 'root'
})
export class AppPwaLaunch implements OnDestroy {
  /** Targets older than this are no longer opened automatically — the user has moved on. */
  private readonly LAUNCH_TARGET_MAX_AGE_DAYS = 7;

  private readonly appMonitoring = inject(AppMonitoring);
  private readonly router = inject(Router);

  private readonly subscriptions = new Subscription();

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Starts recording campaign viewer visits. Call this once, from the root component. Covers all
   * viewers generically, so a new viewer needs no changes here.
   */
  registerForCampaignVisits(): void {
    // The current URL is captured immediately as well: on a cold deep link the first NavigationEnd
    // may already have fired by the time the root component initialises.
    this.recordCampaignVisit(this.router.url);

    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => this.recordCampaignVisit(event.urlAfterRedirects))
    );
  }

  /** Records the URL as the launch target when it is a campaign viewer URL; otherwise ignores it. */
  recordCampaignVisit(url: string): void {
    const target = this.parseViewerUrl(url);
    if (!target) {
      return;
    }

    this.writeLaunchTarget(target);
    this.appMonitoring.logEvent(this, 'PWA launch target recorded', {
      mediaType: target.mediaType,
      campaignId: target.campaignId
    });
  }

  /**
   * The campaign an installed app should open on launch, or null when there is nothing recent enough
   * to open. Expired and unusable records are purged rather than left to accumulate.
   */
  resolveLaunchTarget(): ModelPwaLaunchTarget | null {
    const target = this.readLaunchTarget();
    if (!target) {
      return null;
    }

    // A record that cannot be turned into a valid viewer URL would land the user on the 404 route.
    const isUsable =
      !!target.organizationId &&
      !!target.campaignId &&
      (Object.values(MediaTypes) as string[]).includes(target.mediaType);

    if (!isUsable || this.ageInDays(target.savedAtUtc) > this.LAUNCH_TARGET_MAX_AGE_DAYS) {
      this.appMonitoring.logEvent(this, 'Discarding an unusable or expired PWA launch target', {
        mediaType: target.mediaType,
        savedAtUtc: target.savedAtUtc
      });
      this.clearLaunchTarget();
      return null;
    }

    return target;
  }

  /** The router commands that open a launch target's campaign viewer. */
  buildViewerCommands(target: ModelPwaLaunchTarget): string[] {
    return [NavigationRoutes.viewers.path, target.mediaType, target.organizationId, target.campaignId];
  }

  /**
   * The navigation extras that belong with `buildViewerCommands`. Without them the viewer reopens at
   * its first step, discarding progress the URL was carrying — the subscription topic selection, for
   * one.
   */
  buildViewerNavigationExtras(target: ModelPwaLaunchTarget): UrlCreationOptions {
    return { queryParams: target.queryParams ?? {} };
  }

  clearLaunchTarget(): void {
    try {
      LocalStorageHelper.objects.removeObject(Enums.LocalStorageData.pwaLaunchTargetKey);
    } catch (error: unknown) {
      this.logStorageFailure(error, 'Failed to clear the PWA launch target');
    }
  }

  /**
   * Extracts the campaign from a `view/<mediaType>/<organizationId>/<campaignId>` URL. Returns null
   * for anything else, including the admin area and the landing page.
   */
  private parseViewerUrl(url: string): ModelPwaLaunchTarget | null {
    // Strip the query string and fragment, then split into path segments.
    const segments = url
      .split(/[?#]/)[0]
      .split('/')
      .filter((segment) => !!segment);

    if (segments.length !== 4 || segments[0] !== NavigationRoutes.viewers.path) {
      return null;
    }

    const [, mediaType, organizationId, campaignId] = segments;
    if (!(Object.values(MediaTypes) as string[]).includes(mediaType)) {
      return null;
    }

    return {
      mediaType,
      organizationId: decodeURIComponent(organizationId),
      campaignId: decodeURIComponent(campaignId),
      queryParams: this.parseQueryParams(url),
      savedAtUtc: new Date().toISOString()
    };
  }

  /** The URL's query parameters, or undefined when it carries none. The fragment is not part of it. */
  private parseQueryParams(url: string): Record<string, string> | undefined {
    const queryString = url.split('?')[1]?.split('#')[0];
    if (!queryString) {
      return undefined;
    }

    const queryParams: Record<string, string> = {};
    new URLSearchParams(queryString).forEach((value: string, key: string) => {
      queryParams[key] = value;
    });

    return Object.keys(queryParams).length ? queryParams : undefined;
  }

  private writeLaunchTarget(target: ModelPwaLaunchTarget): void {
    try {
      LocalStorageHelper.objects.setObject<ModelPwaLaunchTarget>(Enums.LocalStorageData.pwaLaunchTargetKey, target);
    } catch (error: unknown) {
      // Private browsing refuses writes; the app just loses the safety net.
      this.logStorageFailure(error, 'Failed to record the PWA launch target');
    }
  }

  private readLaunchTarget(): ModelPwaLaunchTarget | null {
    try {
      return LocalStorageHelper.objects.getObject<ModelPwaLaunchTarget>(Enums.LocalStorageData.pwaLaunchTargetKey);
    } catch (error: unknown) {
      // A corrupt record must never block the app from booting.
      this.logStorageFailure(error, 'Failed to read the PWA launch target');
      this.clearLaunchTarget();
      return null;
    }
  }

  private ageInDays(savedAtUtc: string): number {
    const savedAt = Date.parse(savedAtUtc);
    if (Number.isNaN(savedAt)) {
      return Number.POSITIVE_INFINITY;
    }
    return (Date.now() - savedAt) / (1000 * 60 * 60 * 24);
  }

  private logStorageFailure(error: unknown, fallbackMessage: string): void {
    this.appMonitoring.logException(this, error instanceof Error ? error : fallbackMessage);
  }
}
